import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {  LogOut, PanelLeftOpen, PanelLeftClose } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAppSelector, useAppDispatch } from '@/store';
import { toggleSidebar } from '@/store/app-slice';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import LanguageSwitcher from './language-switcher';
import { useMemo } from 'react';

export default function Header() {
  const { user, logout: authLogout } = useAuth();
  const { sidebarOpen } = useAppSelector(state => state.app);
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleLogout = () => {
    authLogout();
    toast({
      title: t('messages.success.logout', 'Logged Out'),
      description: t(
        'auth.logoutMessage',
        'You have been successfully logged out.'
      ),
    });
  };

  if (!user) return null;

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-14 sm:h-16 items-center gap-2 sm:gap-4 border-b bg-card/80 px-3 sm:px-4 md:px-6 backdrop-blur-sm'
      )}
      dir={'ltr'}
    >
      {/* Left section: Sidebar toggle + spacer */}
      <div
        className={cn(
          'flex items-center',
          isRTL ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleSidebar}
          className="h-8 w-8 p-0 inline-flex z-50 relative"
          aria-label={
            sidebarOpen
              ? t('navigation.closeSidebar', 'Close sidebar')
              : t('navigation.openSidebar', 'Open sidebar')
          }
        >
          {sidebarOpen ? (
            <PanelLeftClose className="h-5 w-5" />
          ) : (
            <PanelLeftOpen className="h-5 w-5" />
          )}
          <span className="sr-only">
            {sidebarOpen
              ? t('navigation.closeSidebar', 'Close sidebar')
              : t('navigation.openSidebar', 'Open sidebar')}
          </span>
        </Button>
      </div>

      {/* Center spacer */}
      <div className="flex-1"></div>

      {/* Right section: Search + Language + Badge + User menu */}
      <div
        className={cn(
          'flex items-center gap-2 sm:gap-4',
          isRTL ? 'flex-row-reverse' : 'flex-row'
        )}
      >
     

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Current role badge - hidden on very small screens */}
        <div className="hidden sm:block">
          <Badge
            variant="secondary"
            className="text-xs font-medium px-3 py-1 bg-primary/10 text-primary border-primary/20"
          >
            {user.roles?.[0] || 'User'}
          </Badge>
        </div>

        {/* User menu */}
        <DropdownMenu dir={isRTL ? 'ltr' : 'rtl'}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full"
            >
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                <AvatarFallback>
                  {user.name
                    ?.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="sr-only">Toggle user menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={isRTL ? 'start' : 'end'}
            className="w-48 sm:w-56 z-[99999]"
            side="bottom"
            sideOffset={5}
            avoidCollisions={true}
            collisionPadding={16}
            sticky="always"
          >
            <DropdownMenuLabel
              className={cn('text-sm', isRTL ? 'text-right' : 'text-left')}
            >
              <div
                className={cn(
                  'flex flex-col space-y-1',
                  isRTL ? 'items-end' : 'items-start'
                )}
              >
                <p className="font-medium leading-none">{user.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Show role badge on mobile */}
            <div className="sm:hidden px-2 py-2">
              <Badge
                variant="secondary"
                className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary border-primary/20"
              >
                {user.roles?.[0] || 'User'}
              </Badge>
            </div>
            <div className="sm:hidden">
              <DropdownMenuSeparator />
            </div>

            <DropdownMenuItem
              className={cn(
                'text-sm text-red-600 focus:text-red-600 focus:bg-red-50',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
              onClick={handleLogout}
            >
              <LogOut className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
